/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Checkbox, Grid, IconButton, Tooltip } from '@mui/material';

import {
  MDInput,
  MDTypography,
  MDBox,
  MDButton,
  MiniBurgerMenu,
  ProjectDetailsModal,
  AuthGuard,
  ConfirmationModal,
  If,
  FaIcon,
  ShareChartModal
} from '@components/index';

import { RootState } from '@store/store';
import {
  useLazyGetProjectLinkQuery,
  useUpdateProjectMutation
} from '@api/projects';
import { setCurrentProjectData } from '@pages/ProjectBoard/slice';

import {
  containsWhiteSpace,
  getFileType,
  getPageName,
  getUTCTime
} from '@utils/common';
import { sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';
import useCustomNavigate from '@hooks/useCustomNavigate';
import { ProjectDetails } from '@DStypes/project';
import { ChartConfigType } from '@DStypes/chart';
import { FileTypes } from '@constants/common';
import {
  ADD_CHART,
  CLICKED_THEME,
  CLICKED_VIEW_CLEANED_DATA,
  PAGE_VISITED,
  CLICKED_SHARE_PROJECT,
  CLICKED_KNOW_MORE,
  CLICKED_FILTER
} from '@constants/eventNames';

interface ProjectHeaderProps {
  searchText: string;
  selectedChartIds: string[];
  setSelectedChartIds: Dispatch<SetStateAction<string[]>>;
  deleteSelected: () => void;
  toggleConfigurator: (type: number) => void;
  setSearchText: (value: string) => void;
  chartConfigs: ChartConfigType[] | undefined;
  isBoardPreview: boolean;
  onDownloadClick: () => void;
  isGoAutoEnabled: boolean;
  filterTooltip: string;
  calenderTooltip: string;
  setOpenCleanActionModal: Dispatch<SetStateAction<boolean>>;
  handleOpenSlider: (tabIndex: number) => void;
  onAddData: () => void;
  onRefreshData: () => void;
  handleSearchTextKeyDown: () => void;
  onInsightsClick: () => void;
  showRedFlag: boolean;
  setShowRedFlag: Dispatch<SetStateAction<boolean>>;
}

const ProjectHeader = ({
  searchText,
  setSearchText,
  chartConfigs,
  selectedChartIds,
  setSelectedChartIds,
  deleteSelected,
  toggleConfigurator,
  isBoardPreview,
  onDownloadClick,
  isGoAutoEnabled,
  filterTooltip,
  setOpenCleanActionModal,
  handleOpenSlider,
  onAddData,
  handleSearchTextKeyDown,
  onInsightsClick,
  onRefreshData,
  showRedFlag,
  setShowRedFlag
}: ProjectHeaderProps): JSX.Element => {
  const { projectId } = useParams();
  const navigate = useCustomNavigate();
  const dispatch = useDispatch();

  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const combineAppendFlag = useSelector(
    (state: RootState) => state?.AppData?.combineKeyFlag
  );
  const isCombineDataEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.combineAppendFile
  );
  const isUpdateDataEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.manualUpdate
  );
  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);

  const [getProjectLink, { data: projectLink, isLoading, isFetching }] =
    useLazyGetProjectLinkQuery();

  const [updateProject] = useUpdateProjectMutation();

  const showAddData = combineAppendFlag && projectBoard?.currentProjectData;
  const isDemoUser = userDetails?.user.is_demo_user;

  const [isEditToggled, setIsEditToggled] = useState(false);
  const [searchTextValue, setSearchTextValue] = useState(searchText);
  const [searchActive, setSearchActive] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [shareChartModalOpen, setShareChartModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    setSearchText(searchTextValue);
  }, [searchTextValue]);

  useEffect(() => {
    if (
      selectedChartIds
        .sort()
        .toString()
        .includes(
          (chartConfigs || [])
            .map((chartConfig: { id: any }) => chartConfig?.id)
            .sort()
            .toString()
        ) &&
      chartConfigs?.length
    )
      setSelectAll(true);
    else setSelectAll(false);
  }, [selectedChartIds, chartConfigs]);

  useEffect(() => {
    if (isBoardPreview) setSearchActive(true);
  }, [isBoardPreview]);

  useEffect(() => {
    if (projectLink) navigator.clipboard.writeText(projectLink as string);
  }, [isLoading, isFetching]);

  const filterColumns = useMemo(() => {
    if (
      projectBoard?.currentProjectData?.filters ||
      projectBoard?.currentProjectData?.dynamic_filters
    ) {
      const columns = Object.keys(
        projectBoard?.currentProjectData?.filters
      ).concat(Object.keys(projectBoard?.currentProjectData?.dynamic_filters));

      return columns;
    }

    return [];
  }, [
    projectBoard?.currentProjectData?.filters,
    projectBoard?.currentProjectData?.dynamic_filters
  ]);

  const onCloseShareChartModal = () => {
    setShareChartModalOpen(false);
  };

  const handleSelectAll = (value: boolean) => {
    setSelectedChartIds(
      value ? chartConfigs?.map((chartConfig) => chartConfig.id) || [] : []
    );
    setSelectAll(true);
  };

  const handleDelete = () => {
    deleteSelected();
    setOpenDeleteModal(false);
  };

  const onUpdateProjectDetails = (projectDetails: ProjectDetails) => {
    updateProject({ id: projectId, payload: projectDetails });
    setIsEditToggled(false);
    dispatch(setCurrentProjectData({ ...projectDetails }));
  };

  const handleClickAway = () => {
    if (searchActive && !isBoardPreview && searchTextValue?.trim().length === 0)
      setSearchActive(false);
  };

  return (
    <>
      <Grid container display='flex' flexDirection='column'>
        <If condition={!!projectBoard.currentProjectData}>
          <Grid
            container
            display='flex'
            alignItems='center'
            flexWrap='nowrap'
            mt={{ xs: '1rem', sm: '0.8rem' }}
          >
            <MDBox
              display='flex'
              alignItems='center'
              onMouseOver={() => setShowEditIcon(true)}
              onMouseLeave={() => setShowEditIcon(false)}
              width={isBoardPreview ? '100%' : '55%'}
            >
              <Tooltip
                title={
                  projectBoard.currentProjectData?.project_name &&
                  projectBoard.currentProjectData?.project_name?.length <= 50
                    ? ''
                    : projectBoard.currentProjectData?.project_name || ''
                }
                placement='top'
              >
                <MDBox
                  display='flex'
                  alignItems='center'
                  columnGap={0.5}
                  maxWidth='55vw'
                >
                  <MDTypography
                    variant='h3'
                    textGradient
                    color='info'
                    sx={{
                      padding: !isBoardPreview ? '2px 0' : '',
                      paddingTop: isBoardPreview ? '10px' : '',
                      cursor: isBoardPreview ? 'text' : 'pointer',
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: containsWhiteSpace(
                        projectBoard.currentProjectData?.project_name || ''
                      )
                        ? 'normal'
                        : 'break-all'
                    }}
                    onClick={() => {
                      !isBoardPreview && setIsEditToggled(true);
                    }}
                  >
                    {projectBoard.currentProjectData?.project_name || ''}
                    {!isBoardPreview && (
                      <FaIcon
                        icon='pencil'
                        typographyProps={{
                          textGradient: true,
                          color: 'dark',
                          variant: 'lg'
                        }}
                        style={{
                          opacity: showEditIcon ? '1' : '0',
                          alignSelf: 'flex-end',
                          marginLeft: '5px'
                        }}
                        onClick={() => setIsEditToggled(true)}
                      />
                    )}
                  </MDTypography>
                </MDBox>
              </Tooltip>
            </MDBox>
            {!isBoardPreview && (
              <>
                <MDBox
                  display='flex'
                  justifyContent='flex-end'
                  sx={{ width: { xs: '100%', sm: '45%' } }}
                >
                  <Tooltip
                    title='Click to generate automated insights'
                    placement='top'
                  >
                    <IconButton
                      size='small'
                      onClick={onInsightsClick}
                      sx={{
                        mr: '3px'
                      }}
                    >
                      <FaIcon
                        icon='lightbulb-on'
                        typographyProps={{ color: 'primary', variant: 'xl' }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title='Download a PDF/PPT report and/or cleaned data file'
                    placement='top'
                  >
                    <IconButton
                      size='small'
                      onClick={onDownloadClick}
                      sx={{
                        mr: '3px'
                      }}
                    >
                      <FaIcon
                        icon='download'
                        typographyProps={{ color: 'dark', variant: 'xl' }}
                      />
                    </IconButton>
                  </Tooltip>
                  <MDBox display={{ xs: 'none', md: 'flex' }}>
                    <Tooltip title='View File information' placement='top'>
                      <IconButton
                        onClick={() => {
                          setOpenCleanActionModal(true);
                          sendAmplitudeData(CLICKED_VIEW_CLEANED_DATA);
                        }}
                        sx={{ mr: '10px' }}
                      >
                        <FaIcon
                          icon='table-layout'
                          typographyProps={{ color: 'dark', variant: 'xl' }}
                        />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                  {getFileType(
                    projectBoard?.currentProjectData?.project_datafiles || []
                  ) === 'GSHEET' && (
                    <Tooltip
                      title={
                        <>
                          <span>
                            Use this feature to automatically add new rows from{' '}
                            {projectBoard.currentProjectData?.project_name ||
                              ''}{' '}
                            and update all charts (if any) with the newly added
                            data. Note that only new rows will be imported.
                          </span>
                          <br />
                          <br />
                          <span>
                            If you want to combine data from another G-sheet or
                            a different sheet in same G-sheet, use “Combine
                            Data”
                          </span>
                        </>
                      }
                      placement='top'
                    >
                      <MDButton
                        size='small'
                        onClick={onRefreshData}
                        color='light'
                        sx={{
                          mr: '10px',
                          p: 0.8
                        }}
                      >
                        {subscriptionFlag &&
                          (isUpdateDataEnabledByPlan ? (
                            <FaIcon
                              icon='lock-open'
                              style={{ marginRight: '7px' }}
                              typographyProps={{
                                variant: 'xs',
                                color: 'primary',
                                mt: '-1.5px'
                              }}
                            />
                          ) : (
                            <FaIcon
                              icon='lock'
                              style={{ marginRight: '7px' }}
                              typographyProps={{
                                variant: 'xs',
                                color: 'primary',
                                mt: '-1.5px'
                              }}
                            />
                          ))}
                        <MDTypography
                          textTransform='capitalize'
                          variant='sm'
                          color='info'
                          fontWeight='bold'
                        >
                          UPDATE DATA
                        </MDTypography>
                      </MDButton>
                    </Tooltip>
                  )}
                  {showAddData && (
                    <Tooltip
                      title={
                        <div style={{ textAlign: 'left' }}>
                          <div>Use this feature to:</div>
                          <ul
                            style={{
                              listStyleType: 'disc',
                              paddingLeft: '20px'
                            }}
                          >
                            <li>
                              Merge different data files, such as combining
                              sales data from January with February.
                            </li>
                            <li>
                              Combine columns from different files using a
                              common ID or name.
                            </li>
                            <li>
                              Link to a different tab within the same Google
                              Sheet (note: this option is available for Google
                              Sheet projects only).
                            </li>
                            <li>
                              Automatically update project data using &apos;
                              Connect Data.&apos;
                            </li>
                          </ul>
                          Any existing charts and downloadable data files will
                          automatically update with new data.{' '}
                          <a
                            href='https://www.notion.so/datasquirrel/What-does-Combine-Data-do-748d2f782e5c4cd99b64a5a66c67da3c?pvs=4'
                            target='_blank'
                            rel='noreferrer'
                            style={{
                              color: 'white',
                              textDecoration: 'underline',
                              textUnderlineOffset: 2
                            }}
                            onClick={() =>
                              sendAmplitudeData(CLICKED_KNOW_MORE, {
                                'Feature Name': 'Combine Data'
                              })
                            }
                          >
                            Click here to learn more.
                          </a>
                        </div>
                      }
                      placement='top'
                    >
                      <MDButton
                        size='small'
                        onClick={onAddData}
                        color='light'
                        sx={{
                          mr: '10px',
                          p: 0.8
                        }}
                      >
                        {subscriptionFlag &&
                          (isCombineDataEnabledByPlan ? (
                            <FaIcon
                              icon='lock-open'
                              style={{ marginRight: '7px' }}
                              typographyProps={{
                                variant: 'xs',
                                color: 'primary',
                                mt: '-1.5px'
                              }}
                            />
                          ) : (
                            <FaIcon
                              icon='lock'
                              style={{ marginRight: '7px' }}
                              typographyProps={{
                                variant: 'xs',
                                color: 'primary',
                                mt: '-1.5px'
                              }}
                            />
                          ))}
                        <FaIcon
                          icon='plus-square'
                          typographyProps={{
                            color: 'primary',
                            variant: 'xl'
                          }}
                          style={{ paddingLeft: 1 }}
                        />
                        <MDTypography
                          textTransform='capitalize'
                          variant='sm'
                          color='info'
                          fontWeight='bold'
                          pl={1}
                        >
                          COMBINE DATA
                        </MDTypography>
                      </MDButton>
                    </Tooltip>
                  )}
                  <Tooltip title='Share this project' placement='top'>
                    <MDButton
                      color='primary'
                      size='small'
                      variant='gradient'
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        marginTop: { xs: '10px', md: '0' },
                        padding: 0.8
                      }}
                      onClick={() => {
                        setShareChartModalOpen(true);
                        sendAmplitudeData(PAGE_VISITED, {
                          'Page Name': 'Share Project Modal',
                          Navigation: getPageName()
                        });
                        sendGAData(PAGE_VISITED, {
                          'Page Name': 'Share Project Modal',
                          Navigation: getPageName()
                        });
                        sendAmplitudeData(CLICKED_SHARE_PROJECT);
                        sendGAData(CLICKED_SHARE_PROJECT);
                      }}
                    >
                      <FaIcon
                        icon='share'
                        typographyProps={{ color: 'white', variant: 'xl' }}
                      />
                      <MDTypography
                        textTransform='capitalize'
                        variant='sm'
                        color='white'
                        fontWeight='bold'
                        pl={1}
                      >
                        SHARE
                      </MDTypography>
                    </MDButton>
                  </Tooltip>
                </MDBox>
                <AuthGuard is_demo_user={isDemoUser}>
                  <Grid item xs={1} display={{ xs: 'block', md: 'none' }}>
                    <MiniBurgerMenu
                      items={[
                        { label: 'Download', icon: 'download' },
                        {
                          label: 'Share',
                          icon: 'share',
                          onClick: () => setShareChartModalOpen(true)
                        },
                        { label: 'Table', icon: 'table' },
                        { label: 'Add to file', icon: 'file-medical' }
                      ]}
                    />
                  </Grid>
                </AuthGuard>
              </>
            )}
          </Grid>

          <Grid item mb='0.5rem' maxWidth='95vw'>
            {(!isBoardPreview ||
              (isBoardPreview &&
                projectBoard.currentProjectData?.project_short_desc)) && (
              <MDBox display='flex' flexDirection='row' alignItems='center'>
                <Tooltip
                  title={
                    projectBoard.currentProjectData?.project_short_desc || ''
                  }
                  placement='bottom-start'
                  onClick={() => {
                    setShowRedFlag(false);
                    !isBoardPreview && setIsEditToggled(true);
                  }}
                >
                  <MDTypography
                    width='fit-content'
                    variant='h5'
                    color='info'
                    sx={{
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      opacity: projectBoard.currentProjectData
                        ?.project_short_desc
                        ? 1
                        : 0.5,
                      cursor: isBoardPreview ? 'text' : 'pointer',
                      wordBreak: 'normal'
                    }}
                  >
                    {projectBoard.currentProjectData?.project_short_desc ||
                      'Add a description to the project (it will appear in downloaded project report)'}
                  </MDTypography>
                </Tooltip>
                {showRedFlag && (
                  <IconButton
                    sx={{ transform: 'rotate(180deg)', p: 0, mt: 0.5, ml: 0.5 }}
                    onClick={() => setShowRedFlag(false)}
                  >
                    <Tooltip
                      title={
                        <>
                          {`Your description may need an update as you've modified certain parameters of this project. You might want to review, and/or view updated insights by DataSquirrel by selecting the  `}
                          <FaIcon
                            icon='lightbulb-on'
                            typographyProps={{ color: 'white', variant: 'lg' }}
                            style={{ pointerEvents: 'none', marginLeft: 5 }}
                          />
                        </>
                      }
                    >
                      <span>
                        <FaIcon
                          icon='play'
                          typographyProps={{ variant: 'lg', color: 'error' }}
                        />
                      </span>
                    </Tooltip>
                  </IconButton>
                )}
              </MDBox>
            )}
            {projectBoard.currentProjectData?.project_long_desc &&
              !projectBoard?.currentProjectData?.project_long_desc.startsWith(
                'Junk Data:'
              ) && (
                <Tooltip
                  title={
                    projectBoard.currentProjectData?.project_long_desc || ''
                  }
                  placement='bottom-start'
                >
                  <MDTypography
                    width='fit-content'
                    variant='h5'
                    color='info'
                    sx={{
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'normal'
                    }}
                  >
                    {projectBoard.currentProjectData?.project_long_desc || ''}
                  </MDTypography>
                </Tooltip>
              )}
            {isBoardPreview && (
              <MDTypography
                width='fit-content'
                variant='h5'
                color='info'
                marginBottom={1}
              >
                {projectBoard?.currentProjectData?.project_datafiles?.[0]
                  ?.filetype === FileTypes.GSHEET ||
                projectBoard?.currentProjectData?.is_api_connected
                  ? `Data last updated on: ${getUTCTime(
                      projectBoard?.currentProjectData
                        ?.project_data_updated_at as string
                    )}`
                  : `Last Updated: ${getUTCTime(
                      (projectBoard?.currentProjectData
                        ?.updated_at as string) ||
                        (projectBoard?.currentProjectData?.created_at as string)
                    )}`}
              </MDTypography>
            )}
          </Grid>
        </If>
        <If condition={!projectBoard.currentProjectData}>
          <Grid container height='100px'></Grid>
        </If>
      </Grid>
      <Card
        sx={{
          mb: 2,
          overflow: 'hidden'
        }}
      >
        <MDBox p='10px' bgColor='secondary' variant='gradient'>
          <Grid
            container
            alignItems='center'
            sx={{ justifyContent: isBoardPreview ? 'center' : 'space-between' }}
            spacing={{ xs: 2, md: 1 }}
          >
            <Grid
              item
              display='flex'
              xs={12}
              lg={isBoardPreview ? 0 : 3.3}
              alignItems='center'
              justifyContent={{
                xs: 'center',
                lg: isBoardPreview ? 'center' : 'flex-start'
              }}
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            >
              {!isBoardPreview && (
                <>
                  <Tooltip title='Select all charts' placement='bottom'>
                    <Checkbox
                      color='primary'
                      checked={selectAll}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        handleSelectAll(event.target.checked)
                      }
                      disabled={chartConfigs?.length === 0}
                    />
                  </Tooltip>
                  <Tooltip
                    title={
                      selectedChartIds.length
                        ? 'Delete selected charts below'
                        : 'Select charts to delete'
                    }
                    placement='bottom'
                  >
                    <span>
                      <IconButton
                        onClick={() => setOpenDeleteModal(true)}
                        disabled={!selectedChartIds.length}
                      >
                        <MDTypography
                          color={!selectedChartIds.length ? 'inherit' : 'white'}
                          variant='xl'
                        >
                          <FontAwesomeIcon icon='trash-can' color='inherit' />
                        </MDTypography>
                      </IconButton>
                    </span>
                  </Tooltip>
                  <AuthGuard is_demo_user={isDemoUser}>
                    <IconButton>
                      <MDTypography color='white' variant='xl'>
                        <FontAwesomeIcon icon='file-export' color='inherit' />
                      </MDTypography>
                    </IconButton>
                  </AuthGuard>
                </>
              )}
              <ClickAwayListener onClickAway={handleClickAway}>
                <MDBox display='flex' alignItems='center'>
                  <MDInput
                    variant='outlined'
                    bgColor='white'
                    placeholder='Find by Chart/Column Name or a Value (Eg: a city’s name)'
                    value={searchTextValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchTextValue(e.target.value)
                    }
                    needSearchIcon
                    sx={{
                      width: searchActive
                        ? { sm: 'calc(60vw - 216px)', lg: '460px' }
                        : { xs: '100%', sm: 0 },
                      opacity: searchActive ? 1 : { xs: 1, sm: 0 },
                      transition: searchActive ? 'width 1s' : '1s'
                    }}
                    onClear={() => setSearchTextValue('')}
                    onKeyDown={(
                      event: React.KeyboardEvent<HTMLInputElement>
                    ) => {
                      if (event.key === 'Enter') handleSearchTextKeyDown();
                    }}
                  />

                  <IconButton
                    onClick={() => setSearchActive(true)}
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      opacity: searchActive ? 0 : 1,
                      transition: searchActive ? 'none' : '3s'
                    }}
                  >
                    <MDTypography color='white' variant='xl'>
                      <FontAwesomeIcon icon='search' color='inherit' />
                    </MDTypography>
                  </IconButton>
                </MDBox>
              </ClickAwayListener>
            </Grid>
            <Grid
              item
              display='flex'
              // xs={12}
              // lg={2.7}
              justifyContent={{ xs: 'center', lg: 'flex-end' }}
              alignItems={'center'}
              sx={{
                position: isBoardPreview ? 'absolute' : '',
                right: '16px'
              }}
            >
              {!isBoardPreview && (
                <Grid
                  container
                  display='flex'
                  justifyContent='flex-end'
                  alignItems='center'
                  alignContent='center'
                  flexWrap='nowrap'
                >
                  <Tooltip title='Select to add a new chart' placement='top'>
                    <MDButton
                      color='light'
                      size='small'
                      onClick={() => {
                        sendAmplitudeData(ADD_CHART, {
                          'Go Auto Enabled': isGoAutoEnabled ? 'Yes' : 'No'
                        });
                        sendGAData(ADD_CHART, {
                          'Go Auto Enabled': isGoAutoEnabled ? 'Yes' : 'No'
                        });
                        navigate(`/chart/${projectId}/new`);
                      }}
                    >
                      <FaIcon
                        icon='plus-square'
                        typographyProps={{
                          color: 'primary',
                          variant: 'xl'
                        }}
                      />
                      <MDTypography
                        color='info'
                        textTransform='capitalize'
                        variant='sm'
                        fontWeight='bold'
                        pl={1}
                      >
                        NEW CHART
                      </MDTypography>
                    </MDButton>
                  </Tooltip>
                  <MDTypography
                    color='white'
                    variant='h5'
                    sx={{ fontSize: '25px', cursor: 'default', ml: 1 }}
                  >
                    |
                  </MDTypography>
                  <Grid ml={1}>
                    <Tooltip title='Change Project Theme' placement='left'>
                      <IconButton
                        onClick={() => {
                          sendAmplitudeData(CLICKED_THEME);
                          handleOpenSlider(0);
                        }}
                      >
                        <FaIcon
                          icon={['far', 'paintbrush']}
                          typographyProps={{
                            color: 'white',
                            variant: 'xl'
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  {/* <Grid>
                    <Tooltip
                      title={
                        calenderTooltip?.length
                          ? calenderTooltip
                          : 'Select any column to filter'
                      }
                      placement='left'
                    >
                      <IconButton
                        onClick={() => {
                          handleOpenSlider(1);
                          sendAmplitudeData(CLICKED_DATE_FILTER_ICON);
                        }}
                      >
                        <FaIcon
                          icon={['far', '']}
                          typographyProps={{
                            color: 'white',
                            variant: 'xl'
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid> */}
                  <Grid>
                    <Tooltip
                      title={
                        filterColumns.length > 0
                          ? filterTooltip
                          : 'Select any column to filter'
                      }
                      placement='left'
                    >
                      <IconButton
                        onClick={() => {
                          handleOpenSlider(1);
                          sendAmplitudeData(CLICKED_FILTER);
                        }}
                      >
                        <FaIcon
                          icon={[
                            filterColumns.length ? 'fas' : 'far',
                            'filter'
                          ]}
                          typographyProps={{
                            color: 'white',
                            variant: 'xl'
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  {/* )} */}
                  <Grid>
                    <Tooltip title='View/Add Project comments' placement='left'>
                      <IconButton onClick={() => handleOpenSlider(2)}>
                        <FaIcon
                          icon={['far', 'comment-dots']}
                          typographyProps={{
                            color: 'white',
                            variant: 'xl'
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              )}
              {isBoardPreview && (
                <Tooltip title='View/Add Project comments' placement='bottom'>
                  <IconButton
                    sx={{
                      display: {
                        xs: isBoardPreview ? 'none' : 'inline-block',
                        lg: 'inline-block'
                      }
                    }}
                    onClick={() => toggleConfigurator(2)}
                  >
                    <FaIcon
                      icon='comment-dots'
                      typographyProps={{ color: 'white', variant: 'lg' }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </MDBox>
      </Card>
      {isEditToggled && (
        <ProjectDetailsModal
          open={isEditToggled}
          onClose={() => setIsEditToggled(false)}
          onUpdateProjectDetails={onUpdateProjectDetails}
        />
      )}
      <ConfirmationModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onProceedButtonClick={handleDelete}
        title='Delete selected chart(s) / KPI(s) ?'
        subtitle='Are you sure you want to delete selected chart(s) / KPI(s) ?'
        proceedButtonMessage='YES, DELETE'
        cancelButtonText='NO, CANCEL'
      />
      <ShareChartModal
        open={shareChartModalOpen}
        onClose={onCloseShareChartModal}
        onClickGetLink={() => getProjectLink(projectId as string)}
        chartLink={projectLink}
        isProject={true}
      />
    </>
  );
};

export default ProjectHeader;
